import Vue from 'vue'

export function cleanErrorMessage(msg) {
  var jsonMatch = msg.match(/response:\s*\n?\s*(\{[\s\S]*\})\s*(?:\(truncated\.\.\.\))?\s*$/)
  if (jsonMatch) {
    try {
      var parsed = JSON.parse(jsonMatch[1])
      if (parsed.message) return parsed.message
    } catch (e) { /* truncated JSON */ }
  }

  var afterResponse = msg.match(/resulted in a `[^`]+` response:\s*\n?\s*([\s\S]+)$/)
  if (afterResponse) {
    var content = afterResponse[1].trim().replace(/\(truncated\.\.\.\)\s*$/, '')
    try {
      var p2 = JSON.parse(content)
      if (p2.message) return p2.message
    } catch (e) { /* truncated JSON */ }
  }

  var msgFieldMatch = msg.match(/"message"\s*:\s*"([^"]+)"/)
  if (msgFieldMatch) return msgFieldMatch[1]

  return msg
}

export function extractDeepErrorMessage(err, fallback) {
  if (!fallback) fallback = 'Something went wrong'
  var data = (err && err.response && err.response.data) || err
  var deepest = data && data.response && data.response.response

  if (deepest && deepest.body && typeof deepest.body === 'string') {
    try {
      var parsed = JSON.parse(deepest.body)
      if (parsed.message) return parsed.message
    } catch (e) { /* not valid JSON */ }
  }

  if (deepest && deepest.error && typeof deepest.error === 'string') {
    var cleaned = cleanErrorMessage(deepest.error)
    if (cleaned !== deepest.error) return cleaned
  }

  var msg = (deepest && deepest.message)
    || (data && data.response && data.response.message)
    || (data && data.message)
    || ''

  if (!msg) return fallback
  return cleanErrorMessage(msg)
}

/**
 * API error helper — Vue 2 equivalent of useApiErrors.ts
 */
export function useApiErrors() {
  var state = Vue.observable({
    lastError: null,
    showError: false,
  })

  function collectBackendErrors(node, accMsgs, accFields) {
    if (!accMsgs) accMsgs = []
    if (!accFields) accFields = {}
    if (!node || typeof node !== 'object') return { accMsgs: accMsgs, accFields: accFields }

    function addMsg(msg) {
      if (msg && typeof msg === 'string' && !accMsgs.includes(msg)) {
        accMsgs.push(cleanErrorMessage(msg))
      }
    }

    addMsg(node.message)
    addMsg(node.error)

    if (node.errors) {
      if (Array.isArray(node.errors)) {
        node.errors.forEach(function (err) {
          var txt = (err && typeof err === 'object' && (err.detail || err.title)) || err
          if (typeof txt === 'string') addMsg(txt)
          var pointer = err && err.source && err.source.pointer && err.source.pointer.split('/').pop()
          if (pointer && typeof txt === 'string') {
            accFields[pointer] = (accFields[pointer] || []).concat(txt)
          }
        })
      } else if (typeof node.errors === 'object') {
        Object.values(node.errors).forEach(function (value) {
          var arr = Array.isArray(value) ? value : [value]
          arr.filter(function (m) { return typeof m === 'string' && m.length > 0 })
            .forEach(function (m) { addMsg(m) })
        })
      }
    }

    if (node.response) {
      collectBackendErrors(node.response, accMsgs, accFields)
    }

    return { accMsgs: accMsgs, accFields: accFields }
  }

  function handleRequestError(error, translate) {
    var payload = (error && error.response && error.response.data) || error || {}
    var result = collectBackendErrors(payload)

    if (!result.accMsgs.length) {
      state.lastError = null
      state.showError = false
      return null
    }

    var t = translate || function (k) { return k }
    var firstMsg = result.accMsgs[0]
    var translated = t('ERRORS.' + firstMsg)
    var title = translated !== 'ERRORS.' + firstMsg ? translated : firstMsg

    var display = {
      title: title,
      details: result.accMsgs.slice(1),
    }

    state.lastError = display
    state.showError = true

    return display
  }

  function dismissError() {
    state.showError = false
    state.lastError = null
  }

  return {
    get lastError() { return state.lastError },
    get showError() { return state.showError },
    handleRequestError: handleRequestError,
    dismissError: dismissError,
  }
}
